import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap, uploadFile, type Schemas } from './client';

export type SsoSettings = Schemas['SsoSettings'];
export type SsoSettingsRequest = Schemas['SsoSettingsRequest'];
export type Branding = Schemas['Branding'];
export type PublicBranding = Schemas['PublicBranding'];
export type SsoDiscovery = Schemas['SsoDiscovery'];

const wsPath = (ws: string) => ({ params: { path: { workspaceId: ws } } });

export function useSsoSettings(ws: string) {
  return useQuery({
    queryKey: ['workspace', ws, 'sso'],
    queryFn: () => unwrap(api.GET('/api/v1/workspaces/{workspaceId}/sso', wsPath(ws))),
  });
}

export function useSaveSsoSettings(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SsoSettingsRequest) => unwrap(api.PUT('/api/v1/workspaces/{workspaceId}/sso', { ...wsPath(ws), body })),
    onSuccess: (data) => qc.setQueryData(['workspace', ws, 'sso'], data),
  });
}

export function useCreateScimToken(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => unwrap(api.POST('/api/v1/workspaces/{workspaceId}/sso/scim-token', wsPath(ws))),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['workspace', ws, 'sso'] }),
  });
}

export function useRevokeScimToken(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => unwrap(api.DELETE('/api/v1/workspaces/{workspaceId}/sso/scim-token', wsPath(ws))),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['workspace', ws, 'sso'] }),
  });
}

export function useBranding(ws: string) {
  return useQuery({
    queryKey: ['workspace', ws, 'branding'],
    queryFn: () => unwrap(api.GET('/api/v1/workspaces/{workspaceId}/branding', wsPath(ws))),
  });
}

export function useSaveBranding(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Schemas['BrandingRequest']) =>
      unwrap(api.PUT('/api/v1/workspaces/{workspaceId}/branding', { ...wsPath(ws), body })),
    onSuccess: (data) => {
      qc.setQueryData(['workspace', ws, 'branding'], data);
      void qc.invalidateQueries({ queryKey: ['workspace', ws] });
    },
  });
}

export function useUploadLogo(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => uploadFile<Branding>(`/api/v1/workspaces/${ws}/branding/logo`, file),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['workspace', ws] }),
  });
}

export function useDeleteLogo(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => unwrap(api.DELETE('/api/v1/workspaces/{workspaceId}/branding/logo', wsPath(ws))),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['workspace', ws] }),
  });
}

/** Branding der Login-Seite nach Host bzw. `?ws=kürzel` (404 → kein Branding). */
export function usePublicBranding(host: string, slug: string | null) {
  return useQuery({
    queryKey: ['public', 'branding', host, slug],
    queryFn: async () => {
      const { data, response } = await api.GET('/api/v1/public/branding', {
        params: { query: { host, slug: slug ?? undefined } },
      });
      return response.ok ? (data ?? null) : null;
    },
    staleTime: Infinity,
    retry: false,
  });
}

export function discoverSso(email: string) {
  return unwrap(api.GET('/api/v1/public/sso/discover', { params: { query: { email } } }));
}
